import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  Skeleton,
  Card,
  CardBody,
  SimpleGrid,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { useGetCreditFacilityMetricsQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { CreditFacility } from '~/contexts/CreditFacilitiesProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { useLoansFilters } from '~/packages/loans/hooks/useLoansFilters';
import { useGetSummarizedLoansQuery } from '~/packages/loans/requests/loans.gql.generated';
import { LoansTable } from '~/packages/loans/ui/LoansTable';
import { useGetPartiesByTypeAndCreditFacilityIdsQuery } from '~/packages/organization/requests/organization.gql.generated';
import { useDeriveTransactionsFiltersFromBackend } from '~/packages/transactions/hooks/transactionsHooks';
import { TransactionsView } from '~/packages/transactions/ui/TransactionsView';

// Constants
const INITIAL_TANSTACK_TABLE_STATE_LOANS = {
  pagination: {
    pageSize: 10,
    pageIndex: 0,
  },
};
const INITIAL_TANSTACK_TABLE_STATE_TRANSACTIONS = {
  pagination: {
    pageSize: 30,
    pageIndex: 0,
  },
};

// Fragments
type SummarySectionItemProps = {
  label: React.ReactNode;
  value: React.ReactNode;
};
const SummarySectionItem = ({ label, value }: SummarySectionItemProps) => (
  <Flex direction="column" gap="0.5rem" flexGrow={1}>
    <Typography variant="detail-muted">{label}</Typography>
    <Typography variant="base" fontWeight="medium">
      {value}
    </Typography>
  </Flex>
);

type SummarySectionProps = { creditFacility: CreditFacility };
const SummarySection = ({ creditFacility }: SummarySectionProps) => {
  const facilityMetricsQuery = useGetCreditFacilityMetricsQuery({
    variables: {
      creditFacilityId: creditFacility.id,
      postingDate: commonDate.currentDateStr(),
    },
  });

  if (facilityMetricsQuery.error) throw facilityMetricsQuery.error;

  return (
    <Flex direction="column" width="100%" gap="1rem">
      <Typography variant="section-title" trim>
        Summary
      </Typography>
      {(() => {
        if (
          !facilityMetricsQuery.data?.metrics ||
          facilityMetricsQuery.loading
        ) {
          return <Skeleton h="12.5rem" w="100%" />;
        }

        const {
          outstandingPrincipalBalance,
          utilization,
          totalAccruedInterest,
          totalAccruedServiceFees,
          commitmentAmount,
          externalFacilityId,
        } = facilityMetricsQuery.data.metrics;

        return (
          <Card variant="outline" size="lg" flexGrow={1}>
            <CardBody display="flex">
              <SimpleGrid
                columns={{ sm: 2, md: 4, lg: 4, xl: 4 }}
                flexGrow={1}
                gap="1.5rem"
              >
                <SummarySectionItem
                  label="Borrower"
                  value={creditFacility.borrowerEntityName}
                />
                {/* @jendevelops TODO pipe in borrower organization into cached cf */}
                {/* <SummarySectionItem label="Industry" value="PLACEHOLDER" /> */}
                <SummarySectionItem
                  label="Accrued Interest & Fees"
                  value={dollarNumberWithCommas(
                    // TODO: @markhaghani - replace with totalAccruedInterestAndFees
                    totalAccruedInterest + totalAccruedServiceFees,
                  )}
                />
                <SummarySectionItem
                  label="Commitment"
                  value={dollarNumberWithCommas(commitmentAmount)}
                />
                <SummarySectionItem
                  label="Outstanding Balance"
                  value={dollarNumberWithCommas(outstandingPrincipalBalance)}
                />
                <SummarySectionItem
                  label="Utilization"
                  value={percentageToPercentageLabel(utilization * 100, 2)}
                />
                <SummarySectionItem
                  label="External Facility ID"
                  value={externalFacilityId ?? '-'}
                />
              </SimpleGrid>
            </CardBody>
          </Card>
        );
      })()}
    </Flex>
  );
};

type LoansSectionProps = { creditFacilityId: string };
const LoansSection = ({ creditFacilityId }: LoansSectionProps) => {
  const [currentFilters, setCurrentFilters] = useLoansFilters({
    creditFacilityIds: [creditFacilityId],
  });

  const { data: lendersData } = useGetPartiesByTypeAndCreditFacilityIdsQuery({
    variables: {
      creditFacilityIds: currentFilters.selectedFacilityIds,
      partyType: CreditAgreement.DomainEntity.Items.PartyType.CapitalProvider,
    },
  });

  useEffect(() => {
    setCurrentFilters({
      lenderIds:
        lendersData?.partiesByTypeAndCreditFacilityIds.map(
          (parties) => parties.id,
        ) ?? [],
    });
  }, [setCurrentFilters, lendersData]);

  const summarizedLoansQuery = useGetSummarizedLoansQuery({
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        creditFacilityIds: currentFilters.selectedFacilityIds,
        capitalProviderIds: currentFilters.selectedLenderIds,
        calculatedLoanStatuses: currentFilters.selectedLoanStatuses,
        isFinal: true,
        createdAtDates: {
          startDate: currentFilters.selectedOriginationDateRange.startDate,
          endDate: currentFilters.selectedOriginationDateRange.endDate,
        },
        maturityDates: {
          startDate: currentFilters.selectedMaturityDateRange.startDate,
          endDate: currentFilters.selectedMaturityDateRange.endDate,
        },
      },
    },
  });
  if (summarizedLoansQuery.error) throw summarizedLoansQuery.error;

  return (
    <Flex direction="column" width="100%">
      <Typography variant="section-title" trim>
        Loans
      </Typography>
      {summarizedLoansQuery.loading ? (
        <Skeleton h="12.5rem" w="100%" />
      ) : (
        <LoansTable
          initialTanstackTableState={INITIAL_TANSTACK_TABLE_STATE_LOANS}
          loading={summarizedLoansQuery.loading}
          loans={summarizedLoansQuery.data?.loans ?? []}
        />
      )}
    </Flex>
  );
};

type TransactionsSectionProps = { creditFacilityId: string };
const TransactionsSection = ({
  creditFacilityId,
}: TransactionsSectionProps) => {
  // Filters
  const result = useDeriveTransactionsFiltersFromBackend({
    creditFacilityIds: [creditFacilityId],
  });

  if (result.status === 'error') throw result.error;

  return (
    <Flex direction="column" width="100%">
      <Typography variant="section-title" trim>
        Transactions
      </Typography>
      {(() => {
        if (result.status === 'pending') {
          return <Skeleton h="12.5rem" w="100%" pt="1rem" />;
        }
        if (result.uniqueTAccounts.length === 0) {
          return (
            <Alert status="warning" pt="1rem">
              <AlertIcon />
              <AlertDescription>
                No account types found for facility
              </AlertDescription>
            </Alert>
          );
        }
        return (
          <TransactionsView
            filterDerivedState={result.currentFilters}
            initialTanstackTableState={
              INITIAL_TANSTACK_TABLE_STATE_TRANSACTIONS
            }
          />
        );
      })()}
    </Flex>
  );
};

// Exports
type PortfolioSummaryProps = { creditFacility: CreditFacility };
export const PortfolioSummary = ({ creditFacility }: PortfolioSummaryProps) => {
  return (
    <Flex direction="column" gap="2.5rem">
      <SummarySection creditFacility={creditFacility} />
      <LoansSection creditFacilityId={creditFacility.id} />
      <TransactionsSection creditFacilityId={creditFacility.id} />
    </Flex>
  );
};
